import React from 'react';
import { DatePicker, Select, Button } from 'antd';
import { RotateCcw } from 'lucide-react';
import { FilterBar, FilterField } from '../../common';
import { t } from '../../../company/labels';

const { RangePicker } = DatePicker;

// Human-readable labels for the `rol` (User.userType) dimension.
const ROL_LABELS = {
    promotor: t('promotor'),
    asesor: 'Asesor',
    auditor: 'Auditor',
    user: 'Usuario',
    admin: 'Admin',
};

export const rolLabel = (value) => ROL_LABELS[value] || value;

// Default (no-op) filter state.
export const EMPTY_POLL_FILTERS = {
    dateRange: null,
    asesor: [],
    region: [],
    cliente: [],
    rol: [],
    canal: [],
};

// Applies the active filters to the prepared polls array. Multi-selects match on
// "any of the selected values"; the date range matches on createdAt (inclusive).
export function applyPollFilters(polls, filters) {
    if (!filters) return polls;
    const { dateRange, asesor, region, cliente, rol, canal } = filters;
    const from = dateRange?.[0] ? dateRange[0].startOf('day') : null;
    const to = dateRange?.[1] ? dateRange[1].endOf('day') : null;

    const anyOf = (selected, value) => !selected?.length || selected.includes(value);

    return polls.filter((p) => {
        if (from || to) {
            const created = p.createdAt ? new Date(p.createdAt).getTime() : null;
            if (created === null) return false;
            if (from && created < from.valueOf()) return false;
            if (to && created > to.valueOf()) return false;
        }
        if (!anyOf(asesor, p.asesor)) return false;
        if (!anyOf(cliente, p.cliente)) return false;
        if (!anyOf(rol, p.rol)) return false;
        if (!anyOf(canal, p.canal)) return false;
        if (region?.length) {
            const regions = Array.isArray(p.regions) ? p.regions : [];
            if (!region.some((r) => regions.includes(r))) return false;
        }
        return true;
    });
}

// Distinct, sorted, non-empty option values for a plain-string field.
const distinct = (polls, key) =>
    [...new Set(polls.map((p) => p[key]).filter((v) => v !== undefined && v !== null && v !== ''))].sort((a, b) =>
        String(a).localeCompare(String(b))
    );

// Distinct values flattened out of an array field (e.g. regions).
const distinctFromArray = (polls, key) =>
    [...new Set(polls.flatMap((p) => (Array.isArray(p[key]) ? p[key] : [])).filter(Boolean))].sort((a, b) =>
        String(a).localeCompare(String(b))
    );

const toOptions = (values, labelFn) => values.map((v) => ({ value: v, label: labelFn ? labelFn(v) : v }));

export default function PollFilters({ polls, filters, onChange }) {
    const options = React.useMemo(
        () => ({
            asesor: toOptions(distinct(polls, 'asesor')),
            region: toOptions(distinctFromArray(polls, 'regions')),
            cliente: toOptions(distinct(polls, 'cliente')),
            rol: toOptions(distinct(polls, 'rol'), rolLabel),
            canal: toOptions(distinct(polls, 'canal')),
        }),
        [polls]
    );

    const set = (patch) => onChange({ ...filters, ...patch });

    const selectProps = {
        allowClear: true,
        showSearch: true,
        optionFilterProp: 'label',
        mode: 'multiple',
        maxTagCount: 'responsive',
        className: 'w-full'
    };

    const hasActive =
        (filters.dateRange && (filters.dateRange[0] || filters.dateRange[1])) ||
        filters.asesor?.length ||
        filters.region?.length ||
        filters.cliente?.length ||
        filters.rol?.length ||
        filters.canal?.length;

    return (
        <FilterBar
            className="!mb-0"
            actions={
                hasActive ? (
                    <Button icon={<RotateCcw />} onClick={() => onChange(null)}>
                        Limpiar filtros
                    </Button>
                ) : null
            }>
            <FilterField label="Rango de fechas" width={250}>
                <RangePicker className="w-full" format="DD/MM/YYYY" value={filters.dateRange} onChange={(dates) => set({ dateRange: dates })} />
            </FilterField>
            <FilterField label="Asesor que evaluó" width={200}>
                <Select {...selectProps} placeholder="Todos" options={options.asesor} value={filters.asesor} onChange={(v) => set({ asesor: v })} />
            </FilterField>
            <FilterField label="Región" width={180}>
                <Select {...selectProps} placeholder="Todas" options={options.region} value={filters.region} onChange={(v) => set({ region: v })} />
            </FilterField>
            <FilterField label="Cliente" width={200}>
                <Select {...selectProps} placeholder="Todos" options={options.cliente} value={filters.cliente} onChange={(v) => set({ cliente: v })} />
            </FilterField>
            <FilterField label="Rol" width={160}>
                <Select {...selectProps} placeholder="Todos" options={options.rol} value={filters.rol} onChange={(v) => set({ rol: v })} />
            </FilterField>
            <FilterField label="Canal" width={160}>
                <Select {...selectProps} placeholder="Todos" options={options.canal} value={filters.canal} onChange={(v) => set({ canal: v })} />
            </FilterField>
        </FilterBar>
    );
}
